import { useCallback, useEffect, useState } from "react";
import { listSavedChannels, saveChannel, unsaveChannel } from "@/lib/api";

/**
 * Tiny shared cache of the user's saved channel_ids so every leaderboard
 * row knows its starred state without each row firing its own API call.
 *
 * Subscribers are React setState fns; we notify them on every mutation.
 */
const subscribers = new Set();
let cachedIds = null; // Set<string> | null (null = not loaded yet)
let inFlight = null;

async function ensureLoaded() {
  if (cachedIds) return cachedIds;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const rows = await listSavedChannels();
      cachedIds = new Set(rows.map((r) => r.channel_id));
    } catch {
      cachedIds = new Set();
    }
    inFlight = null;
    return cachedIds;
  })();
  return inFlight;
}

function notify() {
  subscribers.forEach((fn) => fn(new Set(cachedIds || [])));
}

export function useSavedChannelIds() {
  const [ids, setIds] = useState(cachedIds ? new Set(cachedIds) : new Set());

  useEffect(() => {
    let mounted = true;
    ensureLoaded().then((s) => {
      if (mounted) setIds(new Set(s));
    });
    subscribers.add(setIds);
    return () => {
      mounted = false;
      subscribers.delete(setIds);
    };
  }, []);

  const toggle = useCallback(async (channel) => {
    await ensureLoaded();
    const isSaved = cachedIds.has(channel.channel_id);
    if (isSaved) {
      cachedIds.delete(channel.channel_id);
      notify();
      try {
        await unsaveChannel(channel.channel_id);
      } catch (err) {
        cachedIds.add(channel.channel_id); // rollback
        notify();
        throw err;
      }
    } else {
      cachedIds.add(channel.channel_id);
      notify();
      try {
        await saveChannel({
          channel_id: channel.channel_id,
          name: channel.name,
          description: channel.description,
          thumbnail: channel.thumbnail,
          subscribers: channel.subscribers,
          est_monthly_revenue: channel.est_monthly_revenue,
          avg_video_length_sec: channel.avg_video_length_sec,
          format: channel.format,
          niche: channel.niche,
          channel_age_days: channel.channel_age_days,
          last_upload: channel.last_upload,
          uploads_per_month: channel.uploads_per_month,
          video_count: channel.video_count,
          url: channel.url,
          source: channel.source || "top_revenue",
        });
      } catch (err) {
        cachedIds.delete(channel.channel_id); // rollback
        notify();
        throw err;
      }
    }
    return !isSaved;
  }, []);

  return { ids, toggle };
}

export function invalidateSavedChannels() {
  cachedIds = null;
  inFlight = null;
}
